import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft } from 'lucide-react'
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/src/renderer/components/ui/breadcrumb'
import './SettingsSubpage.scss'

interface SettingsSubpageProps {
  /** Die Seite, von der die Unterseite abgeht — erster Brotkrumen, z. B. „Backups“. */
  pageTitle: string
  /** Titel der Unterseite: letzter Brotkrumen und Überschrift. */
  title: string
  onBack: () => void
  /** Solange etwas läuft, sind Zurück und Brotkrumen gesperrt — sonst ginge der Ablauf samt Ergebnis verloren. */
  locked?: boolean
  children: ReactNode
}

/**
 * **Rahmen einer Unterseite in den Einstellungen** (#158): Zurück-Button und Brotkrumen über dem
 * Inhalt. Jeder Ablauf (Backup erstellen, Wiederherstellen, Todoist-Import, Export) öffnet eine
 * Unterseite statt eines Dialogs.
 *
 * Beim Öffnen geht der Fokus auf den Titel — es sei denn, ein Feld im Inhalt hat ihn sich schon geholt
 * (`autoFocus`, etwa das Passwort). Beim Zurück geht er auf das, was die Unterseite geöffnet hat —
 * dafür hält die Seite ihre Übersicht während der Unterseite versteckt statt sie abzubauen.
 *
 * @example
 * {view === 'create' && (
 *   <SettingsSubpage pageTitle={t('settings.subNav.backups')} title={t('preferences.backups.createTitle')} onBack={() => setView(null)}>
 *     …
 *   </SettingsSubpage>
 * )}
 */
export function SettingsSubpage({ pageTitle, title, onBack, locked = false, children }: SettingsSubpageProps) {
  const { t } = useTranslation()
  const rootRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  /*
    Der Öffner wird beim ersten Rendern gemerkt, nicht im Effekt: Unter StrictMode (Einstellungsfenster)
    läuft der Effekt zweimal, und dazwischen lässt sich der Button nicht fokussieren — er liegt in der
    versteckten Übersicht. Der zweite Lauf hätte sonst den Titel als Öffner gemerkt (Diff-Audit 11.10.2026).
  */
  const [opener] = useState(() => (document.activeElement instanceof HTMLElement ? document.activeElement : null))

  useEffect(() => {
    // `autoFocus` greift beim Einhängen, also vor diesem Effekt — dann bleibt der Fokus im Feld.
    if (!rootRef.current?.contains(document.activeElement)) titleRef.current?.focus()
    return () => {
      if (opener?.isConnected) opener.focus()
    }
  }, [opener])

  return (
    <div ref={rootRef} className="settings-subpage">
      <div className="settings-subpage__bar">
        <button type="button" className="settings-subpage__back" onClick={onBack} disabled={locked}>
          <ChevronLeft size={16} aria-hidden="true" />
          {t('preferences.subpage.back')}
        </button>
        <Breadcrumb aria-label={t('preferences.subpage.breadcrumbLabel')} className="settings-subpage__crumbs">
          <BreadcrumbList>
            <BreadcrumbItem>
              <button type="button" className="settings-subpage__crumb" onClick={onBack} disabled={locked}>
                {pageTitle}
              </button>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <h2 ref={titleRef} tabIndex={-1} className="settings-subpage__title">{title}</h2>
      <div className="settings-subpage__body">{children}</div>
    </div>
  )
}
